'use client';

import { type ReactNode, useCallback, useEffect, useRef } from 'react';

/**
 * Stage — the small runtime the lit sections share.
 *
 * Three jobs, one observer each, and no library:
 *
 * 1. **Arrival.** Everything marked `data-rise` is armed (hidden) and released
 *    when it reaches the viewport. Items that arrive in the same batch are
 *    ordered by where they sit on screen rather than in the DOM — top-left
 *    first, with a slight bias to the left — so a grid lights up as a diagonal
 *    sweep instead of a row-by-row list. The batch position is published as
 *    `--i`; the stylesheet turns it into a delay, which lets each section pick
 *    its own step without touching this file.
 *
 * 2. **Pointer.** The cursor position over the nearest `data-lit` ancestor is
 *    written as `--px` / `--py`, in element coordinates. One delegated listener
 *    for a whole grid, one layout read per painted frame, and nothing at all on
 *    a touch device.
 *
 * 3. **Live.** `data-live` is on the root only while the section is on screen
 *    in a visible tab — the gate for ambient loops, so nothing animates into a
 *    background tab.
 *
 * Anything already on screen when the page hydrates is simply shown: arming it
 * after it has painted would flash. Under `prefers-reduced-motion` everything
 * is shown at once, the pointer is never read and `data-live` is never set.
 */

const REDUCED = '(prefers-reduced-motion: reduce)';
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

/** Nothing waits for more than this many steps, however large the batch. */
const MAX_STEP = 8;

type StageProps = {
  /** The element to render. Lists keep their semantics — this is a wrapper,
   *  not a replacement for the markup underneath it. */
  as?: 'div' | 'ul' | 'ol';
  className?: string;
  children: ReactNode;
  /** Track the pointer over `[data-lit]` descendants. */
  pointer?: boolean;
  /** Gate ambient loops on `[data-live]`. */
  live?: boolean;
};

export function Stage({
  as = 'div',
  className,
  children,
  pointer = false,
  live = false,
}: StageProps) {
  const rootRef = useRef<HTMLElement | null>(null);

  /* A callback ref, so one ref can serve `ul`, `ol` and `div` alike. */
  const setRoot = useCallback((node: HTMLElement | null) => {
    rootRef.current = node;
  }, []);

  /* --- Arrival ------------------------------------------------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-rise]'));
    if (items.length === 0) return;

    if (window.matchMedia(REDUCED).matches) {
      for (const item of items) item.dataset.inview = '';
      return;
    }

    /* Read every rect first, then write: one layout pass, not one per item. */
    const onScreen = items.map((item) => {
      const rect = item.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    });

    const pending: HTMLElement[] = [];
    items.forEach((item, index) => {
      if (onScreen[index]) {
        /* Already painted — show it as it is. Arming now would blink. */
        item.dataset.inview = '';
        return;
      }
      item.dataset.armed = '';
      pending.push(item);
    });

    if (pending.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        const arrived = entries.filter((entry) => entry.isIntersecting);
        if (arrived.length === 0) return;

        /* Screen order, not source order: a card in the far column of the
           first row should not wait behind the whole row above it. The left
           bias is what turns a row sweep into a diagonal one. */
        arrived.sort(
          (a, b) =>
            a.boundingClientRect.top +
            a.boundingClientRect.left * 0.35 -
            (b.boundingClientRect.top + b.boundingClientRect.left * 0.35),
        );

        arrived.forEach((entry, index) => {
          const item = entry.target as HTMLElement;
          item.style.setProperty('--i', String(Math.min(index, MAX_STEP)));
          item.dataset.inview = '';
          io.unobserve(item);
        });
      },
      /* Held back from the very bottom edge, so nothing arrives while it is
         still half a line into the fold. */
      { rootMargin: '0px 0px -12% 0px' },
    );

    for (const item of pending) io.observe(item);
    return () => io.disconnect();
  }, []);

  /* --- Pointer ------------------------------------------------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !pointer) return;
    if (window.matchMedia(REDUCED).matches) return;
    if (!window.matchMedia(FINE_POINTER).matches) return;

    let target: HTMLElement | null = null;
    let clientX = 0;
    let clientY = 0;
    let raf = 0;

    const write = () => {
      raf = 0;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = clientX - rect.left;
      const y = clientY - rect.top;
      target.style.setProperty('--px', `${x.toFixed(1)}px`);
      target.style.setProperty('--py', `${y.toFixed(1)}px`);
      /* The same reading again, as −1…1 from the centre: lights want the
         position, a tilt wants the direction. */
      target.style.setProperty('--nx', (0.5 - x / rect.width).toFixed(3));
      target.style.setProperty('--ny', (0.5 - y / rect.height).toFixed(3));
    };

    const onMove = (event: PointerEvent) => {
      const node = event.target;
      target =
        node instanceof Element
          ? node.closest<HTMLElement>('[data-lit]')
          : null;
      if (!target) return;

      clientX = event.clientX;
      clientY = event.clientY;
      /* The rect is read in the frame, never in the event: one layout read
         per painted frame instead of one per move. */
      if (!raf) raf = requestAnimationFrame(write);
    };

    const onLeave = () => {
      target = null;
    };

    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', onLeave, { passive: true });

    return () => {
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
      for (const lit of root.querySelectorAll<HTMLElement>('[data-lit]')) {
        lit.style.removeProperty('--px');
        lit.style.removeProperty('--py');
      }
    };
  }, [pointer]);

  /* --- Live ---------------------------------------------------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !live) return;
    if (window.matchMedia(REDUCED).matches) return;

    let visible = false;
    const sync = () => {
      if (visible && document.visibilityState === 'visible') {
        root.dataset.live = '';
      } else {
        delete root.dataset.live;
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = Boolean(entry?.isIntersecting);
        sync();
      },
      { rootMargin: '120px 0px' },
    );
    io.observe(root);
    document.addEventListener('visibilitychange', sync);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', sync);
      delete root.dataset.live;
    };
  }, [live]);

  if (as === 'ul') {
    return (
      <ul ref={setRoot} className={className}>
        {children}
      </ul>
    );
  }

  if (as === 'ol') {
    return (
      <ol ref={setRoot} className={className}>
        {children}
      </ol>
    );
  }

  return (
    <div ref={setRoot} className={className}>
      {children}
    </div>
  );
}
